import CompliancePage, {
  CompliancePageContent,
} from '@comp/compliance/compliance-template'

const en = (text: string) => ({ en: text, tr: '' })

const content: CompliancePageContent = {
  path: '/compliance/nis2',
  bilingual: false,
  seoTitle: en('NIS2 Compliance: Identity & Access Controls | Monofor'),
  seoDescription: en(
    'Meet NIS2 Article 21 cybersecurity measures with Monofor: MFA, access control policies, privileged access management, and audit evidence for essential and important entities.'
  ),
  heroEyebrow: en('NIS2 compliance'),
  heroTitle: en('NIS2 asks for MFA by name. Start there.'),
  heroBody: en(
    'The NIS2 Directive explicitly lists multi-factor authentication and access control policies among its required cybersecurity measures, with management personally accountable. Monofor enforces those controls across your estate and produces the evidence your national authority will ask for.'
  ),
  contextHeading: en('Where NIS2 meets identity'),
  contextBody: en(
    'NIS2 (Directive (EU) 2022/2555) widened EU cybersecurity regulation to thousands of "essential" and "important" entities (energy, transport, health, digital infrastructure, manufacturing, public administration, and more), with member-state laws applying from October 2024. Article 21 lists the minimum measures every entity must take, and identity appears by name: access control policies, multi-factor or continuous authentication solutions, and human resources security.\n\nUnlike its predecessor, NIS2 puts management bodies personally on the hook for approving and overseeing these measures, with fines up to 2% of global turnover for essential entities. That changes the conversation: boards now need controls that are demonstrably in place: enforced by systems and documented by logs, not described in policy documents.'
  ),
  requirementsHeading: en('Measure to control, mapped.'),
  requirementsBody: en(
    'Article 21(2) measures with an identity dimension, and the Monofor capability that enforces each one.'
  ),
  requirements: [
    {
      requirement: en('Multi-factor authentication (Art. 21(2)(j))'),
      detail: en(
        'NIS2 explicitly requires MFA or continuous authentication solutions where appropriate.'
      ),
      capability: en(
        'Monosign enforces MFA on every login (TOTP, push, SMS, and phishing-resistant FIDO2 passkeys), with adaptive step-up when risk signals such as new devices or unusual locations appear.'
      ),
      productHref: '/sign/mfa',
      productName: 'Monosign MFA',
    },
    {
      requirement: en('Access control policies (Art. 21(2)(i))'),
      detail: en(
        'Policies on access control and asset management are a named minimum measure.'
      ),
      capability: en(
        'Centralized SSO puts every application behind one policy point; conditional access rules enforce who connects from where, on which device, at what time, and log every decision.'
      ),
      productHref: '/sign',
      productName: 'Monosign',
    },
    {
      requirement: en('Human resources security (Art. 21(2)(i))'),
      detail: en(
        'Access must track the employment lifecycle, especially departures.'
      ),
      capability: en(
        'Joiner-mover-leaver automation provisions access on day one and revokes everything the day someone leaves, with directory sync keeping HR, AD, and applications consistent.'
      ),
      productHref: '/sync',
      productName: 'Monosync',
    },
    {
      requirement: en('Privileged & administrative access'),
      detail: en(
        'Admin access to essential services is the path incident reports keep finding.'
      ),
      capability: en(
        'Monopam vaults privileged credentials, requires approval for elevation, limits it to time-bound windows, and records every session (video plus keystrokes) for incident reconstruction.'
      ),
      productHref: '/pam',
      productName: 'Monopam',
    },
    {
      requirement: en('Supply chain security (Art. 21(2)(d))'),
      detail: en(
        'Entities must manage the security of supplier and service-provider access.'
      ),
      capability: en(
        'Monopam Vendor Privileged Access covers the third-party lifecycle end to end: invitations pass an approval flow before any account exists, enrollment is a passwordless magic link with MFA enforcement, accounts carry a hard expiry enforced at both the PAM and identity layers, and every vendor session is recorded with the sponsor notified on connect. No standing VPN accounts or shared logins in your supply chain.'
      ),
      productHref: '/pam/vendor-access',
      productName: 'Vendor Privileged Access',
    },
    {
      requirement: en('Incident handling & reporting (Arts. 21, 23)'),
      detail: en(
        'A 24-hour early-warning window assumes you can reconstruct access fast.'
      ),
      capability: en(
        'Tamper-evident authentication logs, entitlement history, and session recordings feed your SIEM and give incident teams the who-what-when within the reporting deadline.'
      ),
      productHref: '/sync/governance',
      productName: 'Monosync Governance',
    },
  ],
  faqs: [
    {
      q: en('Does NIS2 apply to us?'),
      a: en(
        'NIS2 covers "essential" and "important" entities across 18 sectors (from energy, transport, banking infrastructure, and health to digital providers, manufacturing of critical products, and public administration), generally organizations with 50+ employees or €10M+ turnover in those sectors, with some size-independent inclusions. Scope is set by each member state’s transposition, so confirm with legal counsel.'
      ),
    },
    {
      q: en('Does deploying Monofor make us NIS2 compliant?'),
      a: en(
        'No single product achieves NIS2 compliance; the directive also covers risk analysis, business continuity, crisis management, and training. Monofor implements the identity-related Article 21 measures (MFA, access control, privileged and supplier access) and documents them, which addresses several of the named minimum measures directly.'
      ),
    },
    {
      q: en('Why does management accountability change anything?'),
      a: en(
        'Because NIS2 makes management bodies approve and oversee cybersecurity measures, with personal liability. Enforced, logged controls that can be demonstrated in one report are much easier to stand behind than policy PDFs. That is precisely what an identity platform provides.'
      ),
    },
    {
      q: en('How does vendor and third-party access fit Article 21?'),
      a: en(
        'Article 21(2)(d) makes supply-chain security a named minimum measure, and vendor remote access is its most exposed edge. Monopam treats third-party access as a lifecycle: an approval-gated invitation, a time-boxed account with MFA, a recorded session, and an access review at the end. Each stage produces the log evidence: who approved the vendor, when they connected, what they did, and when the access expired. The same controls line up with IEC 62443 remote-access requirements for OT operators in NIS2 scope, and the deployment runs self-hosted or air-gapped where those networks require it.'
      ),
    },
    {
      q: en('We already work on DORA. Is NIS2 extra work?'),
      a: en(
        'They overlap heavily on identity controls; financial entities in DORA scope are largely carved out of NIS2, while their ICT suppliers may face both. The same Monofor controls and evidence serve both frameworks; see our DORA page for that mapping.'
      ),
    },
  ],
}

type Props = { locale: string }

export default function Nis2CompliancePage({ locale }: Props) {
  return <CompliancePage content={content} locale={locale} />
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
